import { useMemo } from 'react';
import {
    User, MapPin, Phone, Mail, Clock, CheckCircle2, X as XIcon,
    Banknote, CreditCard, NotebookText, Plus, CornerDownRight,
} from 'lucide-react';
import ModalShell from '../../components/ui/ModalShell';

/**
 * Detail (neaktivní) zastávky — řidič si klikne na číslovaný dot v itineráři
 * a uvidí položky, čas, platbu, kontakt zákazníka, stav doručení.
 *
 * Pro aktivní zastávku má řidič kompletní obraz v BottomPanel + IkonovyDok,
 * tento sheet je proto určen pro QUICK náhled na ostatní (kontrolu pořadí,
 * položek, …).
 */
export default function ZastavkaDetailSheet({ zastavka, onClose, isDark }) {
    const status = useMemo(() => (zastavka ? deriveStatus(zastavka) : null), [zastavka]);
    if (!zastavka) return null;

    const muted = 'var(--text-secondary)';
    const text = 'var(--text-primary)';

    const castka = Math.round(zastavka.castka_kc ?? 0);
    const castkaCelkem = Math.round(zastavka.castka_celkem ?? castka);
    const body = Math.round(zastavka.placeno_body_kc ?? 0);

    return (
        <ModalShell open onClose={onClose} isDark={isDark} nadpis={`Zastávka ${zastavka.poradi ?? '—'} · ${zastavka.adresa || ''}`} ikona={status.Icon} barva={status.color}>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* Status badge */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', gap: 6,
                        padding: '5px 12px', borderRadius: 999,
                        background: hexToRgba(status.color, 0.20),
                        border: `1px solid ${hexToRgba(status.color, 0.60)}`,
                        color: status.color, fontSize: 11, fontWeight: 800,
                        letterSpacing: 0.1,
                    }}>
                        <status.Icon size={12} strokeWidth={2.5} />
                        {status.label}
                    </div>

                    {/* Zákazník */}
                    <Section title="Zákazník" muted={muted}>
                        <Row Icon={User} text={zastavka.jmeno || '—'} muted={muted} text2={text} bold />
                        <Row Icon={MapPin} text={zastavka.adresa || '—'} muted={muted} text2={text} />
                        {zastavka.telefon && (
                            <Row Icon={Phone} text={zastavka.telefon} muted={muted} text2={text}
                                href={`tel:${zastavka.telefon}`} />
                        )}
                        {zastavka.email && (
                            <Row Icon={Mail} text={zastavka.email} muted={muted} text2={text}
                                href={`mailto:${zastavka.email}`} />
                        )}
                    </Section>

                    {/* Čas */}
                    <Section title="Čas doručení" muted={muted}>
                        <TimeRow
                            label="Slíbeno" value={zastavka.cas_slibeny || '—'}
                            muted={muted} text={text}
                        />
                        {zastavka.stav_doruceni === 'doruceno' && zastavka.cas_doruceni_skutecny ? (
                            <TimeRow
                                label="Doručeno"
                                value={fmtHm(zastavka.cas_doruceni_skutecny)}
                                color={status.color} text={text} muted={muted}
                                rozdilSec={zastavka.rozdil_sec}
                            />
                        ) : (
                            <TimeRow
                                label="Předpoklad"
                                value={fmtHm(zastavka.cas_predpoklad) || '—'}
                                color={status.color} text={text} muted={muted}
                                rozdilSec={zastavka.rozdil_sec}
                            />
                        )}
                    </Section>

                    {/* Platba */}
                    <Section title="Platba" muted={muted}>
                        <PlatbaRow stav={zastavka.stav_platby} castka={castka}
                            castkaCelkem={castkaCelkem} body={body}
                            muted={muted} text={text} />
                    </Section>

                    {/* Položky — hlavní + jejich přídavky */}
                    {Array.isArray(zastavka.polozky) && zastavka.polozky.length > 0 && (
                        <Section title="Položky" muted={muted}>
                            <PolozkySeznam polozky={zastavka.polozky} muted={muted} text={text} />
                        </Section>
                    )}

                    {zastavka.poznamka && (
                        <Section title="Poznámka" muted={muted}>
                            <Row Icon={NotebookText} text={zastavka.poznamka} muted={muted} text2={text} />
                        </Section>
                    )}
                </div>
        </ModalShell>
    );
}

// -------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------

/**
 * Seznam položek se zvýrazněnou hierarchií:
 *   • Hlavní položky = bílý box s počtem (chip), názvem a cenou
 *   • Přídavky (`celkem_s_dph === 0`) = indented řádky pod předchozí hlavní
 *     položkou se "└" konektorem, menší fontem a fialovým akcentem.
 *
 * Tím řidič / pokladní vidí jasně, ke které jídlu/pití přídavek patří.
 */
function PolozkySeznam({ polozky, muted, text }) {
    const groups = useMemo(() => groupPolozky(polozky), [polozky]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {groups.map((g, gi) => (
                <div key={gi} style={{
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 9,
                    overflow: 'hidden',
                }}>
                    {/* Hlavní položka */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                    }}>
                        <Chip count={g.pocet} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontSize: 13, fontWeight: 700, color: text,
                                lineHeight: 1.3, wordBreak: 'break-word',
                            }}>
                                {g.nazev}
                            </div>
                            {g.poznamka && (
                                <div style={{
                                    fontSize: 11, color: muted, marginTop: 2,
                                    fontStyle: 'italic',
                                }}>{g.poznamka}</div>
                            )}
                        </div>
                        <span style={{
                            fontSize: 13, fontWeight: 800, color: text,
                            fontFamily: 'ui-monospace, monospace', letterSpacing: -0.3,
                            whiteSpace: 'nowrap',
                        }}>
                            {Math.round(g.celkem_s_dph).toLocaleString('cs-CZ')} Kč
                        </span>
                    </div>

                    {/* Přídavky — fialovo-tónovaná zóna pod hlavní položkou */}
                    {g.addons.length > 0 && (
                        <div style={{
                            background: 'var(--surface-raised)',
                            borderTop: '1px solid var(--border-default)',
                            padding: '6px 12px 8px',
                            display: 'flex', flexDirection: 'column', gap: 2,
                        }}>
                            <div style={{
                                fontSize: 9, fontWeight: 800, color: 'var(--text-secondary)',
                                letterSpacing: 0.4, textTransform: 'uppercase',
                                marginBottom: 2,
                            }}>
                                Ingredience / úpravy
                            </div>
                            {g.addons.map((a, ai) => (
                                <div key={ai} style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '2px 0',
                                }}>
                                    <CornerDownRight size={11} style={{ color: '#FF8A00', flexShrink: 0 }} />
                                    <span style={{
                                        flex: 1, minWidth: 0,
                                        fontSize: 12, color: 'var(--text-primary)', fontWeight: 600,
                                        wordBreak: 'break-word',
                                    }}>
                                        {a.pocet > 1 ? `${a.pocet}× ` : ''}{a.nazev}
                                    </span>
                                    {a.celkem_s_dph > 0 && (
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
                                            fontFamily: 'ui-monospace, monospace', letterSpacing: -0.2,
                                            display: 'inline-flex', alignItems: 'center', gap: 1,
                                        }}>
                                            <Plus size={9} strokeWidth={3} />
                                            {Math.round(a.celkem_s_dph).toLocaleString('cs-CZ')} Kč
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function Chip({ count }) {
    return (
        <span style={{
            minWidth: 26, height: 26, borderRadius: 6,
            padding: '0 6px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,138,0,0.14)',
            border: '1px solid rgba(255,138,0,0.45)',
            color: '#FF8A00', flexShrink: 0,
            fontSize: 12, fontWeight: 800,
            fontFamily: 'ui-monospace, monospace', letterSpacing: -0.2,
        }}>
            {Number.isInteger(count) ? count : (count || 1)}×
        </span>
    );
}

/**
 * Sgrupuje položky podle vzoru Objedname:
 *   - Hlavní jídla/pití mají `celkem_s_dph > 0`
 *   - Přídavky/modifikátory mají `celkem_s_dph === 0` a patří k POSLEDNÍ
 *     hlavní položce před nimi (sekvenční pořadí v poli zachované scraperem).
 *
 * Položky s cenou > 0 jsou samostatné. Položky s cenou 0 bez předcházející
 * hlavní (vzácné, vadný import) skončí jako vlastní "skupina" — neztratí se.
 */
function groupPolozky(polozky) {
    const groups = [];
    let current = null;
    for (const p of polozky) {
        const isAddon = (p.celkem_s_dph ?? 0) <= 0;
        if (isAddon && current) {
            current.addons.push(p);
        } else {
            current = {
                nazev: p.nazev,
                pocet: p.pocet,
                celkem_s_dph: p.celkem_s_dph,
                poznamka: p.poznamka,
                addons: [],
            };
            groups.push(current);
        }
    }
    return groups;
}

function Section({ title, muted, children }) {
    return (
        <div>
            <div style={{
                fontSize: 10, fontWeight: 800, color: muted,
                letterSpacing: 0.5, textTransform: 'uppercase',
                marginBottom: 6,
            }}>{title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {children}
            </div>
        </div>
    );
}

function Row({ Icon, text, text2, muted, href, bold = false }) {
    const inner = (
        <>
            <Icon size={14} style={{ color: muted, flexShrink: 0 }} />
            <span style={{
                fontSize: 13, color: text2, lineHeight: 1.35,
                fontWeight: bold ? 700 : 500,
                wordBreak: 'break-word',
            }}>{text}</span>
        </>
    );
    if (href) {
        return (
            <a href={href} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                color: 'inherit', textDecoration: 'none', padding: '2px 0',
            }}>{inner}</a>
        );
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
            {inner}
        </div>
    );
}

function TimeRow({ label, value, color = 'var(--text-primary)', muted, text, rozdilSec }) {
    const rozdilMin = rozdilSec != null ? Math.round(rozdilSec / 60) : null;
    const rozdilColor = rozdilMin == null
        ? muted
        : rozdilMin > 0 ? '#EF4444' : '#10B981';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
            <Clock size={14} style={{ color: muted, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: muted, fontWeight: 700, minWidth: 78, letterSpacing: 0.2, textTransform: 'uppercase' }}>
                {label}
            </span>
            <span style={{
                fontSize: 14, color, fontWeight: 800,
                fontFamily: 'ui-monospace, monospace', letterSpacing: -0.3,
            }}>
                {value}
            </span>
            {rozdilMin != null && (
                <span style={{ fontSize: 11, fontWeight: 700, color: rozdilColor, marginLeft: 'auto' }}>
                    {rozdilMin > 0 ? `+${rozdilMin} min` : rozdilMin === 0 ? 'v termínu' : `${rozdilMin} min`}
                </span>
            )}
        </div>
    );
}

function PlatbaRow({ stav, castka, castkaCelkem, body, muted, text }) {
    const meta = {
        online_zaplaceno:  { Icon: CreditCard, label: 'Online zaplaceno', color: '#10B981' },
        zaplaceno_hotove:  { Icon: Banknote,   label: 'Hotově zaplaceno', color: '#10B981' },
        zaplaceno_kartou:  { Icon: CreditCard, label: 'Kartou zaplaceno', color: '#10B981' },
        k_uhrade:          { Icon: Banknote,   label: 'Dobírka — k vybrání', color: '#FF8A00' },
    }[stav ?? 'k_uhrade'] ?? { Icon: Banknote, label: stav, color: '#FF8A00' };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px', borderRadius: 7,
            background: hexToRgba(meta.color, 0.14),
            border: `1px solid ${hexToRgba(meta.color, 0.45)}`,
        }}>
            <meta.Icon size={16} style={{ color: meta.color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: meta.color }}>{meta.label}</div>
                {body > 0 && (
                    <div style={{ fontSize: 10, color: muted, marginTop: 1 }}>
                        Celkem {castkaCelkem.toLocaleString('cs-CZ')} Kč &middot; {body.toLocaleString('cs-CZ')} Kč body
                    </div>
                )}
            </div>
            <span style={{
                fontSize: 17, fontWeight: 800, color: text,
                fontFamily: 'ui-monospace, monospace', letterSpacing: -0.4,
            }}>
                {castka.toLocaleString('cs-CZ')} Kč
            </span>
        </div>
    );
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

function deriveStatus(z) {
    if (z.stav_doruceni === 'doruceno') {
        return { label: 'Doručeno', color: '#10B981', Icon: CheckCircle2 };
    }
    if (z.stav_doruceni === 'nezastizen') {
        return { label: 'Nezastižen', color: '#6B7280', Icon: XIcon };
    }
    return { label: 'Čeká na doručení', color: '#FF8A00', Icon: Clock };
}

function hexToRgba(hex, alpha) {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function fmtHm(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}
